import React, { useEffect, useId, useRef, useState } from "react";
import { Button, Modal, Overlay, Popover } from "react-bootstrap";
import { adminApi } from "../admin/adminApi";
import "./ReferenceExampleLink.css";

const SIGN_IN = "Sign in through Settings to view private reference images, then try again.";

export default function ReferenceExampleLink({ packId, version, slot }) {
  const target = useRef(null);
  const hideTimer = useRef(null);
  const operation = useRef(0);
  const imageUrl = useRef(null);
  const previewId = useId();
  const [preview, setPreview] = useState(false);
  const [opened, setOpened] = useState(false);
  const [fit, setFit] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [data, setData] = useState(null);
  const active = preview || opened;
  const valid = /^[a-f0-9]{64}$/.test(packId || "") && Number.isSafeInteger(version) && version > 0;
  const title = `${slot} · ${slot.startsWith("S") ? "Successful" : "Failed"} reference · version ${version}`;

  const revoke = () => {
    if (imageUrl.current) URL.revokeObjectURL(imageUrl.current);
    imageUrl.current = null;
  };

  useEffect(() => adminApi.subscribeSessionExpired(() => {
    operation.current += 1;
    revoke();
    setData({ error: SIGN_IN });
  }), []);

  useEffect(() => {
    const current = ++operation.current;
    revoke();
    setData(null);
    if (!active || !valid) return undefined;
    // Resolve the immutable version recorded in this assessment, even if a
    // newer version is active or the original pack has since been disabled.
    (async () => {
      try {
        const packs = await adminApi.getReferencePacks();
        if (operation.current !== current) return;
        const saved = packs.find((pack) => pack.id === packId)?.versions
          .find((entry) => entry.version === version)?.slots?.[slot];
        if (!saved?.imageId) throw new Error("The saved reference example is no longer available.");
        const blob = await adminApi.getReferenceImage(saved.imageId);
        if (operation.current !== current) return;
        imageUrl.current = URL.createObjectURL(blob);
        setData({ url: imageUrl.current, note: saved.note || "" });
      } catch (error) {
        if (operation.current !== current) return;
        setData({ error: [401, 403].includes(error?.status) ? SIGN_IN
          : error?.message || "Could not load this reference image. Try again." });
      }
    })();
    return () => { operation.current += 1; revoke(); };
  }, [active, valid, packId, version, slot, attempt]);

  useEffect(() => () => clearTimeout(hideTimer.current), []);
  const show = () => { clearTimeout(hideTimer.current); setPreview(true); };
  const hide = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setPreview(false), 160);
  };
  const open = (event) => {
    event.stopPropagation();
    clearTimeout(hideTimer.current);
    setFit(false);
    setOpened(true);
    setPreview(false);
  };
  const close = () => { setOpened(false); setPreview(false); };
  const status = data?.error
    ? <p role="alert">{data.error}</p>
    : <p role="status">Loading reference image…</p>;

  if (!valid) return <span>{slot} (preview unavailable)</span>;
  return <>
    <button ref={target} type="button" className="referenceExampleLink"
      aria-label={`View ${title}`} aria-haspopup="dialog"
      aria-describedby={preview && !opened ? previewId : undefined}
      onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}
      onKeyDown={(event) => {
        if (event.key === "Escape" && preview) { event.stopPropagation(); setPreview(false); }
      }} onClick={open}>{slot}</button>
    <Overlay target={target.current} show={preview && !opened} placement="auto" flip>
      <Popover id={previewId} className="referenceExamplePopover"
        onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
        <Popover.Header>{title}</Popover.Header>
        <Popover.Body>
          {data?.url ? <button type="button" className="referenceExampleThumbnail" onClick={open}>
            <img src={data.url} alt={title} />
            <span>Click to view full size</span>
          </button> : status}
        </Popover.Body>
      </Popover>
    </Overlay>
    <Modal show={opened} onHide={close} fullscreen className="referenceExampleModal"
      backdropClassName="referenceExampleBackdrop" aria-labelledby={`${previewId}-title`}>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title id={`${previewId}-title`}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {data?.url ? <>
          <div className="referenceExampleToolbar">
            <Button variant="outline-info" aria-pressed={!fit} onClick={() => setFit(false)}>Actual size</Button>
            <Button variant="outline-info" aria-pressed={fit} onClick={() => setFit(true)}>Fit width</Button>
            {data.note && <p>{data.note}</p>}
          </div>
          <div className="referenceExampleImageViewport" tabIndex={0} aria-label="Reference chart; scroll to inspect">
            <img src={data.url} alt={title} className={fit ? "fit" : "actual"} />
          </div>
        </> : <>{status}{data?.error && <Button onClick={() => setAttempt((value) => value + 1)}>Try again</Button>}</>}
      </Modal.Body>
    </Modal>
  </>;
}
